/**
 * sanitizeQuranHtml — minimal HTML sanitizer for API-returned tafsir HTML.
 *
 * Uses DOMParser to create a real DOM tree — this approach is immune to the
 * multi-character bypass vectors that affect regex-based sanitizers (e.g.
 * `<scr<script>ipt>` patterns).
 *
 * Removes: script, object, embed, style, iframe, link, meta elements and
 * all event-handler (on*) attributes and javascript: URIs.
 * Everything else (p, span, b, i, br, etc.) is preserved so that the
 * tafsir formatting from api.quran.com survives intact.
 */
export function sanitizeQuranHtml(html: string): string {
  // Parse into an isolated DOM tree
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Remove entire dangerous elements (including their content)
  const dangerousTags = ['script', 'object', 'embed', 'style', 'iframe', 'link', 'meta', 'base', 'form', 'input', 'button', 'select', 'textarea'];
  for (const tag of dangerousTags) {
    doc.querySelectorAll(tag).forEach(el => el.remove());
  }

  // Walk every remaining element and strip dangerous attributes
  doc.body.querySelectorAll('*').forEach(el => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (
        name.startsWith('on') ||
        ((name === 'href' || name === 'src' || name === 'action') && value.startsWith('javascript:'))
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });

  return doc.body.innerHTML;
}

